import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useLicenseLabels } from "@/hooks/useLicenseLabels";
import type { SubmittedApplication } from "@/pages/apply/hooks/useApplyForm";

interface SuccessPanelProps {
  data: SubmittedApplication;
  onReset: () => void;
}

export default function SuccessPanel({ data, onReset }: SuccessPanelProps) {
  const { t } = useTranslation();
  const { edition } = useLicenseLabels();
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(data.reference);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const details = [
    { label: t("apply.detailProduct"), value: data.productName },
    { label: t("apply.detailVersion"), value: data.productVersion },
    { label: t("apply.detailEdition"), value: edition(data.edition) },
    { label: t("apply.detailSeats"), value: t("apply.seatsUnit", { count: data.seatCount }) },
    { label: t("apply.detailMachine"), value: data.machineCode },
    { label: t("apply.detailEmail"), value: data.email },
  ];

  const nextSteps = [
    { title: t("apply.next1Title"), desc: t("apply.next1Desc") },
    { title: t("apply.next2Title"), desc: t("apply.next2Desc") },
    { title: t("apply.next3Title"), desc: t("apply.next3Desc") },
  ];

  return (
    <div className="lh-fade-up rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
      <div className="flex flex-col items-center text-center">
        <span className="flex h-14 w-14 items-center justify-center rounded-full bg-primary-100 text-primary-700">
          <i className="ri-checkbox-circle-line text-3xl" />
        </span>
        <h2 className="mt-4 font-heading text-xl font-semibold text-foreground-950">{t("apply.successTitle")}</h2>
        <p className="mt-2 max-w-md text-sm leading-relaxed text-foreground-600">{t("apply.successDesc")}</p>
      </div>

      <div className="mt-6 flex flex-col items-center justify-between gap-3 rounded-lg border border-background-200 bg-background-100 px-5 py-4 sm:flex-row">
        <div>
          <p className="text-xs text-foreground-500">{t("apply.refLabel")}</p>
          <p className="mt-1 font-heading text-lg font-semibold tracking-wide text-foreground-950">
            {data.reference}
          </p>
        </div>
        <button
          type="button"
          onClick={handleCopy}
          className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100"
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i className={copied ? "ri-check-line text-primary-600" : "ri-file-copy-line"} />
          </span>
          {copied ? t("apply.copied") : t("apply.copyRef")}
        </button>
      </div>

      <dl className="mt-6 grid gap-x-6 gap-y-4 sm:grid-cols-2">
        {details.map((item) => (
          <div key={item.label} className="border-b border-background-200 pb-3">
            <dt className="text-xs text-foreground-500">{item.label}</dt>
            <dd className="mt-1 text-sm font-medium text-foreground-950">{item.value}</dd>
          </div>
        ))}
      </dl>

      <div className="mt-6 rounded-lg bg-secondary-50 p-5">
        <h3 className="font-heading text-sm font-semibold text-foreground-950">{t("apply.nextTitle")}</h3>
        <ol className="mt-3 space-y-3">
          {nextSteps.map((step, index) => (
            <li key={step.title} className="flex gap-3">
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-500 text-xs font-semibold text-background-50">
                {index + 1}
              </span>
              <div>
                <p className="text-sm font-medium text-foreground-950">{step.title}</p>
                <p className="mt-0.5 text-xs text-foreground-600">{step.desc}</p>
              </div>
            </li>
          ))}
        </ol>
      </div>

      <div className="mt-6 flex flex-col items-center gap-3 sm:flex-row">
        <Link
          to="/licenses"
          className="inline-flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 sm:w-auto"
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-key-2-line" />
          </span>
          {t("apply.viewLicenses")}
        </Link>
        <button
          type="button"
          onClick={onReset}
          className="inline-flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-5 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:bg-background-100 sm:w-auto"
        >
          <span className="flex h-4 w-4 items-center justify-center">
            <i className="ri-add-line" />
          </span>
          {t("apply.submitAnother")}
        </button>
        <Link
          to="/"
          className="inline-flex w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap px-2 py-2.5 text-sm font-medium text-foreground-600 transition-colors hover:text-foreground-950 sm:w-auto"
        >
          {t("apply.backHome")}
        </Link>
      </div>
    </div>
  );
}